import React, { useState } from 'react';
import { Shield, Sparkles, Users, Camera, Zap, Home, Building, Droplets } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { mockServices } from '../mock';

const serviceIcons = {
  1: Home,
  2: Building,
  3: Droplets,
  4: Users,
  5: Zap,
  6: Shield,
  7: Camera
};

export const Services = () => {
  const [activeTab, setActiveTab] = useState('cleaning');

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-red-600">Integrated</span> Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From residential cleaning to commercial security, we provide comprehensive solutions 
            tailored to protect and maintain your environment with excellence.
          </p>
        </div>

        {/* Service Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 h-12">
            <TabsTrigger 
              value="cleaning" 
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white flex items-center space-x-2"
            >
              <Sparkles className="w-4 h-4" />
              <span>Cleaning Services</span>
            </TabsTrigger>
            <TabsTrigger 
              value="security" 
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white flex items-center space-x-2"
            >
              <Shield className="w-4 h-4" />
              <span>Security Services</span>
            </TabsTrigger>
          </TabsList>

          {/* Cleaning Services */}
          <TabsContent value="cleaning" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mockServices.cleaning.map((service) => {
                const IconComponent = serviceIcons[service.id];
                return (
                  <Card key={service.id} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-red-200">
                    <CardHeader className="pb-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors duration-300">
                        <IconComponent className="w-6 h-6 text-red-600 group-hover:text-white" />
                      </div>
                      <CardTitle className="text-xl font-semibold text-gray-900">{service.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-gray-600">{service.description}</p>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-700">Key Features:</p>
                        <div className="flex flex-wrap gap-2">
                          {service.features.map((feature, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Security Services */}
          <TabsContent value="security" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mockServices.security.map((service) => {
                const IconComponent = serviceIcons[service.id];
                return (
                  <Card key={service.id} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-red-200">
                    <CardHeader className="pb-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors duration-300">
                        <IconComponent className="w-6 h-6 text-red-600 group-hover:text-white" />
                      </div>
                      <CardTitle className="text-xl font-semibold text-gray-900">{service.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-gray-600">{service.description}</p>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-700">Key Features:</p>
                        <div className="flex flex-wrap gap-2">
                          {service.features.map((feature, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Need a Custom Solution?
          </h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            We understand every client has unique needs. Let us create a tailored security 
            and cleaning package that perfectly fits your requirements and budget.
          </p>
          <Button 
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg font-semibold"
            onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Get Custom Quote
          </Button>
        </div>
      </div>
    </section>
  );
};